import {useMemo, useState} from "react";
import {useSelector} from "react-redux";

import {PRICES_CITIES} from "@pg-rp/navigation";

import type {IRPStore} from "../../../app/rp_reducer";
import {getLatestSettledPoint, getTransactionPricesQuery, toTransactionPrice} from "../actions/fetch_transaction_prices";
import {getRcnDataApi} from "../api/get_rcn_data_api";
import {getPricesCityRegionId} from "../constants/prices_cities_regions";
import {PRICES_HUB_TRANSACTION_EXCLUDED_CITY_SLUGS} from "../constants/prices_hub_cities";
import type {IPricesSearchCityMetrics, IPricesSearchMetricValues} from "../types/IPricesSearchModel";
import type {ITransactionPrice} from "../types/ITransactionPrice";
import type {RcnRegionsPriceStatsResponse} from "../types/RcnRegionsPriceStats";
import {getPricesAvailableDate} from "../utils/get_prices_available_date";

export interface IUsePricesSearchCityMetricsOptions {
    /** The panel is open. The first open sends the requests; closing it later keeps what was fetched. */
    isOpen: boolean;
    /** Districts rendered in the results. One batch per distinct set, cached by RTK Query. */
    districtRegionIds?: readonly number[];
}

// The same list, deduplicated and sorted, that loadTransactionPrices sends, so on the hub a client request
// would share the SSR cache entry.
const CITIES = PRICES_CITIES.map((city) => ({slug: city.slug, regionId: getPricesCityRegionId(city.slug)})).filter(
    (city): city is typeof city & {regionId: number} => city.regionId !== null
);
const CITY_REGION_IDS = [...new Set(CITIES.map((city) => city.regionId))].sort((prev, next) => prev - next);

// Missing, zero or non-finite values are left out, so a row never renders "0 zł/m²".
const toMetricValues = (price: ITransactionPrice): IPricesSearchMetricValues => {
    const metrics: IPricesSearchMetricValues = {};

    if (price.unit === "per_m2" && Number.isFinite(price.medianPriceM2) && price.medianPriceM2 > 0) {
        metrics.priceM2 = price.medianPriceM2;
    }

    if (Number.isFinite(price.transactionsCount) && price.transactionsCount > 0) {
        metrics.transactionsCount = price.transactionsCount;
    }

    return metrics;
};

// Same reading as the hub table: the reported quarter, once it clears the sample floor.
const getCityPricesFromResponse = (response: RcnRegionsPriceStatsResponse): ITransactionPrice[] => {
    const seriesByRegion = new Map(response.results.map((result) => [result.region_id, result.series]));

    return CITIES.flatMap((city) => {
        const point = getLatestSettledPoint(seriesByRegion.get(city.regionId) ?? []);

        return point ? [toTransactionPrice(city.slug, city.regionId, point)] : [];
    });
};

// Districts have no PRICES_CITIES slug, so the region id stands in for it - only the numbers are read.
const getDistrictPricesFromResponse = (response: RcnRegionsPriceStatsResponse): ITransactionPrice[] =>
    response.results.flatMap((result) => {
        const point = getLatestSettledPoint(result.series);

        return point ? [toTransactionPrice(String(result.region_id), result.region_id, point)] : [];
    });

/**
 * Transaction price per m² and transaction count for the city and district rows of the prices search.
 * Cities come from the store when the page already loaded them (the hub, during SSR); anywhere else one
 * RCN batch goes out on the first open. Districts are read in batches of the ids currently rendered.
 */
export const usePricesSearchCityMetrics = (options: IUsePricesSearchCityMetricsOptions): IPricesSearchCityMetrics => {
    const {isOpen, districtRegionIds = []} = options;

    const [wasOpened, setWasOpened] = useState(isOpen);
    if (isOpen && !wasOpened) {
        setWasOpened(true);
    }

    // The hub table's anchor, so on the hub the client query hits the SSR cache entry.
    const pricesAvailableDate = useSelector((store: IRPStore) => store.report.price.pricesAvailableDate);
    const referenceDate = useMemo(() => getPricesAvailableDate(pricesAvailableDate?.date), [pricesAvailableDate?.date]);
    const storePrices = useSelector((store: IRPStore) => store.report.price.transactionPrices);
    const hasStorePrices = storePrices.length > 0;

    const cityQuery = getRcnDataApi.endpoints.getRcnRegionsData.useQuery(getTransactionPricesQuery(CITY_REGION_IDS, referenceDate), {
        skip: !wasOpened || hasStorePrices
    });

    const districtIds = [...new Set(districtRegionIds)].sort((prev, next) => prev - next);
    const districtQuery = getRcnDataApi.endpoints.getRcnRegionsData.useQuery(getTransactionPricesQuery(districtIds, referenceDate), {
        skip: !wasOpened || districtIds.length === 0
    });

    const cityData = cityQuery.data;
    // `data` outlives an id change, so districts already shown keep their numbers while a larger set loads.
    const districtData = districtQuery.data;
    const isLoading = cityQuery.isFetching || districtQuery.isFetching;

    return useMemo(() => {
        const cityPrices = hasStorePrices ? storePrices : cityData ? getCityPricesFromResponse(cityData) : [];
        const byCitySlug: Record<string, IPricesSearchMetricValues> = {};
        const byRegionId: Record<number, IPricesSearchMetricValues> = {};

        for (const price of cityPrices) {
            // Kept out of the hub table on purpose (PRICES_HUB_TRANSACTION_EXCLUDED_CITY_SLUGS), so no figure here either.
            if (PRICES_HUB_TRANSACTION_EXCLUDED_CITY_SLUGS.some((slug) => slug === price.slug)) {
                continue;
            }

            const metrics = toMetricValues(price);
            byCitySlug[price.slug] = metrics;
            byRegionId[price.regionId] = metrics;
        }

        for (const price of districtData ? getDistrictPricesFromResponse(districtData) : []) {
            byRegionId[price.regionId] = toMetricValues(price);
        }

        return {byCitySlug, byRegionId, isLoading};
    }, [hasStorePrices, storePrices, cityData, districtData, isLoading]);
};
import {useMemo, useState} from "react";
import {useSelector} from "react-redux";

import {PRICES_CITIES} from "@pg-rp/navigation";

import type {IRPStore} from "../../../app/rp_reducer";
import {getLatestSettledPoint, getTransactionPricesQuery, toTransactionPrice} from "../actions/fetch_transaction_prices";
import {getRcnDataApi} from "../api/get_rcn_data_api";
import {getPricesCityRegionId} from "../constants/prices_cities_regions";
import {PRICES_HUB_TRANSACTION_EXCLUDED_CITY_SLUGS} from "../constants/prices_hub_cities";
import type {IPricesSearchCityMetrics, IPricesSearchMetricValues} from "../types/IPricesSearchModel";
import type {ITransactionPrice} from "../types/ITransactionPrice";
import type {RcnRegionsPriceStatsResponse} from "../types/RcnRegionsPriceStats";
import {getPricesAvailableDate} from "../utils/get_prices_available_date";

export interface IUsePricesSearchCityMetricsOptions {
    /** The panel is open. The first open sends the requests; closing it later keeps what was fetched. */
    isOpen: boolean;
    /** Districts rendered in the results. One batch per distinct set, cached by RTK Query. */
    districtRegionIds?: readonly number[];
}

// The same list, deduplicated and sorted, that loadTransactionPrices sends, so on the hub a client request
// would share the SSR cache entry.
const CITIES = PRICES_CITIES.map((city) => ({slug: city.slug, regionId: getPricesCityRegionId(city.slug)})).filter(
    (city): city is typeof city & {regionId: number} => city.regionId !== null
);
const CITY_REGION_IDS = [...new Set(CITIES.map((city) => city.regionId))].sort((prev, next) => prev - next);

// Missing, zero or non-finite values are left out, so a row never renders "0 zł/m²".
const toMetricValues = (price: ITransactionPrice): IPricesSearchMetricValues => {
    const metrics: IPricesSearchMetricValues = {};

    if (price.unit === "per_m2" && Number.isFinite(price.medianPriceM2) && price.medianPriceM2 > 0) {
        metrics.priceM2 = price.medianPriceM2;
    }

    if (Number.isFinite(price.transactionsCount) && price.transactionsCount > 0) {
        metrics.transactionsCount = price.transactionsCount;
    }

    return metrics;
};

// Same reading as the hub table: the reported quarter, once it clears the sample floor.
const getCityPricesFromResponse = (response: RcnRegionsPriceStatsResponse): ITransactionPrice[] => {
    const seriesByRegion = new Map(response.results.map((result) => [result.region_id, result.series]));

    return CITIES.flatMap((city) => {
        const point = getLatestSettledPoint(seriesByRegion.get(city.regionId) ?? []);

        return point ? [toTransactionPrice(city.slug, city.regionId, point)] : [];
    });
};

// Districts have no PRICES_CITIES slug, so the region id stands in for it - only the numbers are read.
const getDistrictPricesFromResponse = (response: RcnRegionsPriceStatsResponse): ITransactionPrice[] =>
    response.results.flatMap((result) => {
        const point = getLatestSettledPoint(result.series);

        return point ? [toTransactionPrice(String(result.region_id), result.region_id, point)] : [];
    });

/**
 * Transaction price per m² and transaction count for the city and district rows of the prices search.
 * Cities come from the store when the page already loaded them (the hub, during SSR); anywhere else one
 * RCN batch goes out on the first open. Districts are read in batches of the ids currently rendered.
 */
export const usePricesSearchCityMetrics = (options: IUsePricesSearchCityMetricsOptions): IPricesSearchCityMetrics => {
    const {isOpen, districtRegionIds = []} = options;

    const [wasOpened, setWasOpened] = useState(isOpen);
    if (isOpen && !wasOpened) {
        setWasOpened(true);
    }

    // The hub table's anchor, so on the hub the client query hits the SSR cache entry.
    const pricesAvailableDate = useSelector((store: IRPStore) => store.report.price.pricesAvailableDate);
    const referenceDate = useMemo(() => getPricesAvailableDate(pricesAvailableDate?.date), [pricesAvailableDate?.date]);
    const storePrices = useSelector((store: IRPStore) => store.report.price.transactionPrices);
    const hasStorePrices = storePrices.length > 0;

    const cityQuery = getRcnDataApi.endpoints.getRcnRegionsData.useQuery(getTransactionPricesQuery(CITY_REGION_IDS, referenceDate), {
        skip: !wasOpened || hasStorePrices
    });

    const districtIds = [...new Set(districtRegionIds)].sort((prev, next) => prev - next);
    const districtQuery = getRcnDataApi.endpoints.getRcnRegionsData.useQuery(getTransactionPricesQuery(districtIds, referenceDate), {
        skip: !wasOpened || districtIds.length === 0
    });

    const cityData = cityQuery.data;
    // `data` outlives an id change, so districts already shown keep their numbers while a larger set loads.
    const districtData = districtQuery.data;
    const isLoading = cityQuery.isFetching || districtQuery.isFetching;

    return useMemo(() => {
        const cityPrices = hasStorePrices ? storePrices : cityData ? getCityPricesFromResponse(cityData) : [];
        const byCitySlug: Record<string, IPricesSearchMetricValues> = {};
        const byRegionId: Record<number, IPricesSearchMetricValues> = {};

        for (const price of cityPrices) {
            // Kept out of the hub table on purpose (PRICES_HUB_TRANSACTION_EXCLUDED_CITY_SLUGS), so no figure here either.
            if (PRICES_HUB_TRANSACTION_EXCLUDED_CITY_SLUGS.some((slug) => slug === price.slug)) {
                continue;
            }

            const metrics = toMetricValues(price);
            byCitySlug[price.slug] = metrics;
            byRegionId[price.regionId] = metrics;
        }

        for (const price of districtData ? getDistrictPricesFromResponse(districtData) : []) {
            byRegionId[price.regionId] = toMetricValues(price);
        }

        return {byCitySlug, byRegionId, isLoading};
    }, [hasStorePrices, storePrices, cityData, districtData, isLoading]);
};
